/* istanbul ignore file */
import React, {
  lazy, Suspense,
} from 'react';
import {
  Navigate, Route, Routes,
} from 'react-router-dom';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { setupLocalization } from 'locales';
import 'styles/themes/index.scss';
import { FpCallback } from 'auth/FpCallback';
import { FpLogin } from 'auth/FpLogin';
import { FpLogout } from 'auth/FpLogout';
import { createBrowserHistory } from 'history';
import { reactPlugin } from 'azure-application-insights';
import { interceptor } from 'api/settings';
import { useAppInsightsUserAttributes } from 'hooks/application-insights-user-hook';
import { useTokenRefresh } from 'hooks/token-refresh';

import { store } from 'redux/store';
import { useUserReadFacade } from 'redux/user-read/user-read.facade';
import { useNotificationFasade } from 'redux/notification/notification.facade';
import { useProjectReadFacade } from 'redux/project-read/project-read.facade';

import { FpAuthGuard } from 'routers/guard/FpAuthGuard';
import { FpSearchGuard } from 'routers/guard/FpSearchGuard';
import { FpAdminGuard } from 'routers/guard/FpAdminGuard';
import { FpAdminExtensiveAttributeValueGuard } from 'routers/guard/FpAdminExtensiveAttributeValueGuard';
import { FpAdminLayoutEditGuard } from 'routers/guard/FpAdminLayoutEditGuard';
import { FpLayout } from 'components/templates/FpLayout';
import { FpLoading } from 'components/atoms/FpLoading';
import { serviceProjectStatusFilter } from 'utils/service-project-filter';
import { CustomBrowserRouter } from 'CustomBrowserRouter';

const FpPortal = lazy(() => import('components/pages/FpPortal'));
const FpLayoutRoleAssign = lazy(() => import('components/pages/FpLayoutRoleAssign'));
const FpLayoutEdit = lazy(() => import('components/pages/layout-edit/FpLayoutEdit'));
const FpLayoutPreview = lazy(() => import('components/pages/layout-edit/FpLayoutPreview'));
const FpMainMenuList = lazy(() => import('components/pages/main-menu-edit/FpMainMenuList'));
const FpProjectSelect = lazy(() => import('components/pages/FpProjectSelect'));
const FpFileSearchQueryResolver = lazy(() => import('components/pages/file-search'));
const FpProjectDetail = lazy(() => import('components/pages/FpProjectDetail'));
const FpExtensiveAttributeValuesEdit = lazy(() => import('components/pages/extensive-attributes/FpExtensiveAttributeValuesEdit'));
const FpMainMenuEditFolderSearch = lazy(() => import('components/pages/main-menu-edit/FpMainMenuEditFolderSearch'));
const FpError = lazy(() => import('components/pages/FpError'));
const FpSearchCriteriaManagement = lazy(() => import('components/pages/FpSearchCriteriaManagement'));
const FpMainMenuEditFileSearch = lazy(() => import('components/pages/main-menu-edit/FpMainMenuEditFileSearch'));
const FpExtensiveAttributeList = lazy(() => import('components/pages/extensive-attributes/FpExtensiveAttributeList'));
const FpExtensiveAttributesContentTagManagement = lazy(() => import('components/pages/extensive-attributes/FpExtensiveAttributesContentTagManagement'));
const FpExtensiveAttributesAdd = lazy(() => import('components/pages/extensive-attributes/FpExtensiveAttributesAdd'));
const FpExtensiveAttributeDefinitionEdit = lazy(() => import('components/pages/extensive-attributes/FpExtensiveAttributeDefinitionEdit'));
const FpLayoutCopy = lazy(() => import('components/pages/layout-copy/FpLayoutCopy'));
const FpLayoutCopyPreview = lazy(() => import('components/pages/layout-copy/FpLayoutCopyPreview'));

// Routerの初期化
const history = createBrowserHistory();
// Axiosの初期化
interceptor(store, history);
// i18nの初期化
setupLocalization();

export default function App() {
  const {
    user,
    locale,
    displayLocale,
    userName,
    companyName,
    isGroupManagerServiceAuth,
    isProjectInvitationServiceAuth,
  } = useUserReadFacade();
  const {
    selectedProject,
    isProjectAdmin,
    isFpReadableProject,
    isFpEditableProject,
    isEditableExtendedAttributeProject,
    isDdmsReadableProject,
    isPhotoReadableProject,
    serviceProjectPolicies,
  } = useProjectReadFacade();
  const {
    notifications,
    removeNotification,
  } = useNotificationFasade();

  useTokenRefresh();

  useAppInsightsUserAttributes();

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <LocalizationProvider language={locale}>
        <IntlProvider locale={locale}>
          <CustomBrowserRouter browserHistory={history}>
            <Suspense fallback={<FpLoading />}>
              <FpLayout
                userName={userName}
                companyName={companyName}
                displayLocale={displayLocale}
                zoneinfo={user.zoneinfo}
                projectName={selectedProject?.project_name || ''}
                projectTypeName={selectedProject?.project_type_name || ''}
                projectStatus={serviceProjectStatusFilter('FILE_PORTAL', selectedProject.service_project_statuses || []) || ''}
                projectId={selectedProject?.project_id || ''}
                isProjectAdmin={isProjectAdmin}
                isFpReadableProject={isFpReadableProject}
                isFpEditableProject={isFpEditableProject}
                isEditableExtendedAttributeProject={isEditableExtendedAttributeProject}
                isDdmsReadableProject={isDdmsReadableProject}
                isPhotoReadableProject={isPhotoReadableProject}
                isProjectInvitationServiceAuth={isProjectInvitationServiceAuth}
                isGroupManagerServiceAuth={isGroupManagerServiceAuth}
                serviceProjectPolicies={serviceProjectPolicies}
                notifications={notifications}
                onCloseNotification={removeNotification}
              >
                <Routes>
                  {/* 認証が不要なRoute */}
                  <Route path="/login" element={<FpLogin />} />
                  <Route path="/callback" element={<FpCallback />} />
                  <Route path="/logout" element={<FpLogout />} />
                  {/* 認証が必要なRoute */}
                  <Route path="/" element={<FpAuthGuard />}>
                    <Route path="/portal" element={<FpPortal />} />
                    <Route path="/project" element={<FpProjectDetail />} />
                    <Route path="/project_select" element={<FpProjectSelect />} />
                    <Route element={<FpSearchGuard />}>
                      <Route path="/search" element={<FpFileSearchQueryResolver />} />
                    </Route>
                    <Route path="/error" element={<FpError />} />
                    {/* 管理者権限が必要なRoute */}
                    <Route element={<FpAdminGuard />}>
                      <Route path="/admin/layout_role_assign" element={<FpLayoutRoleAssign />} />
                      <Route path="/admin/layouts/:layout_id/copy" element={<FpLayoutCopy />} />
                      <Route path="/admin/layouts/:layout_id/copy/preview" element={<FpLayoutCopyPreview />} />
                      <Route path="/admin/search" element={<FpSearchCriteriaManagement />} />
                      <Route path="/admin/extended_attribute" element={<FpExtensiveAttributeList />} />
                      <Route path="/admin/extensive_attributes/add" element={<FpExtensiveAttributesAdd />} />
                      <Route path="/admin/extensive_attributes/:extensive_attribute_definition_id/folder_select" element={<FpExtensiveAttributesContentTagManagement />} />
                      <Route path="/admin/extensive_attributes/:extensive_attribute_definition_id/edit" element={<FpExtensiveAttributeDefinitionEdit />} />
                    </Route>
                    <Route element={<FpAdminExtensiveAttributeValueGuard />}>
                      <Route path="/admin/extensive_attributes/:extensive_attribute_definition_id/values_edit" element={<FpExtensiveAttributeValuesEdit />} />
                    </Route>
                    {/* レイアウト編集中かどうか監視するRoute */}
                    <Route element={<FpAdminLayoutEditGuard />}>
                      <Route path="/admin/layout/edit" element={<FpLayoutEdit />} />
                      <Route path="/admin/layout/preview" element={<FpLayoutPreview />} />
                      <Route path="/admin/layout/edit/main_menu" element={<FpMainMenuList />} />
                      <Route path="/admin/layout/edit/main_menu/file_search" element={<FpMainMenuEditFileSearch />} />
                      <Route path="/admin/layout/edit/main_menu/folder-search" element={<FpMainMenuEditFolderSearch />} />
                    </Route>
                    {/* 該当するパスがなかったらポータルへ飛ばす */}
                    <Route path="/" element={<Navigate to="/portal" replace />} />
                  </Route>
                </Routes>
              </FpLayout>
            </Suspense>
          </CustomBrowserRouter>
        </IntlProvider>
      </LocalizationProvider>
    </AppInsightsContext.Provider>
  );
}
